import { useCallback, useRef, useState } from 'react';

/**
 * Undo/redo history for the workflow canvas.
 *
 * Owns the snapshot stack and the `canUndo`/`canRedo` flags.  Snapshots
 * are taken from the caller-supplied `nodesRef`/`edgesRef` (kept current
 * by the component) so history always reflects the latest graph without
 * this hook subscribing to every change.
 *
 * Extracted from WorkflowBuilder so the (previously untested) history
 * logic can be exercised in isolation.
 *
 * @param {object}   opts
 * @param {object}   opts.nodesRef  - Ref whose `.current` is the live nodes.
 * @param {object}   opts.edgesRef  - Ref whose `.current` is the live edges.
 * @param {Function} opts.setNodes  - ReactFlow nodes setter.
 * @param {Function} opts.setEdges  - ReactFlow edges setter.
 * @param {Function} [opts.onRestore] - Called after an undo/redo applies a
 *   snapshot (e.g. to clear the current node selection).
 * @returns {{
 *   canUndo: boolean, canRedo: boolean,
 *   pushHistory: Function, undo: Function, redo: Function,
 *   resetHistory: Function, clearHistory: Function,
 *   isApplyingRef: object,
 * }}
 */
export function useUndoRedo({
  nodesRef,
  edgesRef,
  setNodes,
  setEdges,
  onRestore,
}) {
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);
  const historyRef = useRef([]);
  const historyIndexRef = useRef(-1);
  // True while an undo/redo is applying a snapshot, so change handlers
  // can skip recording that programmatic change as a new history entry.
  const isApplyingRef = useRef(false);

  const pushHistory = useCallback(() => {
    if (isApplyingRef.current) return;
    const snapshot = {
      nodes: nodesRef.current,
      edges: edgesRef.current,
    };
    const trimmed = historyRef.current.slice(
      0,
      historyIndexRef.current + 1,
    );
    trimmed.push(snapshot);
    historyRef.current = trimmed;
    historyIndexRef.current = trimmed.length - 1;
    setCanUndo(trimmed.length > 1);
    setCanRedo(false);
  }, [nodesRef, edgesRef]);

  const undo = useCallback(() => {
    if (historyIndexRef.current <= 0) return;
    isApplyingRef.current = true;
    historyIndexRef.current -= 1;
    const { nodes: n, edges: e } =
      historyRef.current[historyIndexRef.current];
    setNodes(n);
    setEdges(e);
    setCanUndo(historyIndexRef.current > 0);
    setCanRedo(true);
    isApplyingRef.current = false;
    if (onRestore) onRestore();
  }, [setNodes, setEdges, onRestore]);

  const redo = useCallback(() => {
    const last = historyRef.current.length - 1;
    if (historyIndexRef.current >= last) return;
    isApplyingRef.current = true;
    historyIndexRef.current += 1;
    const { nodes: n, edges: e } =
      historyRef.current[historyIndexRef.current];
    setNodes(n);
    setEdges(e);
    setCanRedo(historyIndexRef.current < last);
    setCanUndo(true);
    isApplyingRef.current = false;
    if (onRestore) onRestore();
  }, [setNodes, setEdges, onRestore]);

  /* Seed history with a single initial snapshot (e.g. on workflow load). */
  const resetHistory = useCallback((nodes, edges) => {
    historyRef.current = [{ nodes, edges }];
    historyIndexRef.current = 0;
    setCanUndo(false);
    setCanRedo(false);
  }, []);

  /* Drop all history (e.g. when no workflow is active). */
  const clearHistory = useCallback(() => {
    historyRef.current = [];
    historyIndexRef.current = -1;
    setCanUndo(false);
    setCanRedo(false);
  }, []);

  return {
    canUndo,
    canRedo,
    pushHistory,
    undo,
    redo,
    resetHistory,
    clearHistory,
    isApplyingRef,
  };
}
